import { ReactNode } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { HeroSection } from "./HeroSection";
import { StatsSection } from "./StatsSection";
import { FeaturesGrid } from "./FeaturesGrid";
import { TechnologiesGrid } from "./TechnologiesGrid";
import { IndustriesSection } from "./IndustriesSection";
import { CTASection } from "./CTASection";
import { ProcessTimeline } from "./ProcessTimeline";
import { LucideIcon } from "lucide-react";

interface ServicePageTemplateProps {
  seo: {
    title: string;
    description: string;
    keywords: string;
    canonical: string;
    structuredData?: object;
    ogImage?: string;
    ogTitle?: string;
    ogDescription?: string;
  };
  hero: {
    badge: { icon: LucideIcon; text: string };
    title: string;
    titleHighlight: string;
    description: string;
    primaryCTA: { text: string; onClick?: () => void };
    secondaryCTA?: { text: string; onClick?: () => void };
    gradient: string;
    accentColor: string;
    orbitIcons: LucideIcon[];
    logo?: { src: string; alt: string };
    backgroundImage?: string;
  };
  stats: {
    items: Array<{ label: string; value: number; suffix: string; icon: LucideIcon }>;
    gradient: string;
  };
  features: {
    title: string;
    subtitle: string;
    badge: { icon: LucideIcon; text: string; color: string };
    items: Array<{ text: string; description?: string; icon: LucideIcon; gradient: string }>;
  };
  technologies?: {
    title: string;
    subtitle?: string;
    badge: { icon: LucideIcon; text: string; color: string };
    items: Array<{ name: string; description?: string; tools: string[] }>;
    checkColor?: string;
  };
  process?: {
    badge: { icon: LucideIcon; text: string; color: string };
    title: string;
    subtitle?: string;
    steps: Array<{ icon: LucideIcon; title: string; description: string; gradient: string }>;
  };
  industries?: {
    badge: { icon: LucideIcon; text: string; color: string };
    title: string;
    subtitle?: string;
    items: Array<{ name: string; icon: LucideIcon; projects: number; percentage: number }>;
    progressGradient: string;
    progressIconColor: string;
    statBoxes: [
      { icon: LucideIcon; value: string; label: string; gradient?: string; iconColor?: string },
      { icon: LucideIcon; value: string; label: string; gradient?: string; iconColor?: string },
      { icon: LucideIcon; value: string; label: string; gradient?: string; iconColor?: string },
      { icon: LucideIcon; value: string; label: string; gradient?: string; iconColor?: string }
    ];
  };
  cta: {
    title: string;
    description: string;
    buttonText: string;
    buttonOnClick?: () => void;
    gradient: string;
    buttonTextColor: string;
  };
  children?: ReactNode; // For custom sections between standard ones
}

export const ServicePageTemplate = ({
  seo,
  hero,
  stats,
  features,
  technologies,
  process,
  industries,
  cta,
  children,
}: ServicePageTemplateProps) => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical={seo.canonical}
        structuredData={seo.structuredData}
        ogImage={seo.ogImage}
        ogTitle={seo.ogTitle}
        ogDescription={seo.ogDescription}
        robots="index, follow"
      />
      <Navigation />
      
      <HeroSection
        badge={hero.badge}
        title={hero.title}
        titleHighlight={hero.titleHighlight}
        description={hero.description}
        primaryCTA={hero.primaryCTA}
        secondaryCTA={hero.secondaryCTA}
        gradient={hero.gradient}
        accentColor={hero.accentColor}
        orbitIcons={hero.orbitIcons}
        logo={hero.logo}
        backgroundImage={hero.backgroundImage}
      />

      <StatsSection stats={stats.items} gradient={stats.gradient} />

      <FeaturesGrid
        title={features.title}
        subtitle={features.subtitle}
        badge={features.badge}
        features={features.items}
      />

      {technologies && (
        <TechnologiesGrid
          title={technologies.title}
          subtitle={technologies.subtitle}
          badge={technologies.badge}
          technologies={technologies.items}
          checkColor={technologies.checkColor}
        />
      )}

      {process && (
        <ProcessTimeline
          badge={process.badge}
          title={process.title}
          subtitle={process.subtitle}
          steps={process.steps}
        />
      )}

      {children}

      {industries && (
        <IndustriesSection
          badge={industries.badge}
          title={industries.title}
          subtitle={industries.subtitle}
          industries={industries.items}
          progressGradient={industries.progressGradient}
          progressIconColor={industries.progressIconColor}
          statBoxes={industries.statBoxes}
        />
      )}

      <CTASection
        title={cta.title}
        description={cta.description}
        buttonText={cta.buttonText}
        buttonOnClick={cta.buttonOnClick}
        gradient={cta.gradient}
        buttonTextColor={cta.buttonTextColor}
      />

      <Footer />
    </div>
  );
};
